// Home board — the "clicked outside customize mode" decision, as ONE pure
// function instead of an inline exclusion list inside pages/main.js.
//
// UI-CUSTOMIZE-SIDEBAR-REOPEN-1 (layer 3, and the reason this file exists):
// the document-level dismisser flipped `_homeCustomizeMode` off on a
// pointerdown anywhere outside the two regions it knew about — the grid and
// the drawer. The tuck/untuck peek handle mounts OUTSIDE the drawer BY
// DESIGN (the drawer translates +120% off-viewport and would carry the peek
// with it — that was layer 1), so the ONE affordance that exists to undo the
// tuck was itself read as "clicked away from the drawer": pointerdown
// dismissed customize mode BEFORE the peek's own click could untuck
// (pointerdown precedes click). The user got a visible control that did the
// opposite of its job, three fixes in a row.
//
// The exclusion list is the ONLY thing standing between that user story and
// its fourth return, and a regex pin on the source text of pages/main.js
// cannot execute it. Here it is a unit: `customizeDismissDecision` takes the
// event target and the live mode flag and returns the decision plus WHY, so
// a test asserts the BEHAVIOUR (a peek pointerdown keeps the mode) instead
// of the spelling of a selector inside a 10k-line page factory.
//
// Pure by construction: no DOM reads beyond `target.closest`, no state
// writes. The caller performs the `root.update`.

// Regions a pointerdown must never be read as a dismissal of customize mode.
// Each entry is load-bearing and each has already cost one shipped bug:
//  - `.home-grid`             the board customize mode exists to edit (select,
//                             drag, resize, remove a widget)
//  - `[data-home-customize-drawer]`  the panel itself (restore a widget, reset)
//  - `[data-home-customize-peek]`    the tucked-drawer recall handle, layer 3
export const CUSTOMIZE_DISMISS_EXCLUSIONS = [
  '.home-grid',
  '[data-home-customize-drawer]',
  '[data-home-customize-peek]'
]

// `{ dismiss, reason }` — `reason` names the region that protected the press
// (or why there was nothing to dismiss), so a caller or a test can say which
// rule fired rather than inferring it from an absent side effect.
export const customizeDismissDecision = (target, customizeMode) => {
  if (!customizeMode) return { dismiss: false, reason: 'not-customizing' }
  if (!target || typeof target.closest !== 'function') {
    return { dismiss: false, reason: 'no-target' }
  }
  for (const selector of CUSTOMIZE_DISMISS_EXCLUSIONS) {
    if (target.closest(selector)) return { dismiss: false, reason: selector }
  }
  return { dismiss: true, reason: 'outside' }
}

// The state patch a dismissal writes. Kept next to the decision because the
// pair is the contract: S7 (2026-08-16) added the second key so a later
// re-open never starts tucked off-canvas from a leftover flag, and an
// explicit exit (the drawer's × / Done, Escape) writes the SAME patch.
export const CUSTOMIZE_EXIT_PATCH = {
  _homeCustomizeMode: false,
  _homeCustomizeDrawerTucked: false
}
